// Deck Types

export interface Deck {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  file_type: string;
  file_size: number;
  storage_path: string;
  original_filename: string;
  thumbnail_path: string | null;
  page_count: number | null;
  current_version: number;
  status: 'active' | 'archived';
  created_at: string;
  updated_at: string;
}

export interface DeckVersion {
  id: string;
  deck_id: string;
  version_number: number;
  file_type: string;
  file_size: number;
  storage_path: string;
  original_filename: string;
  uploaded_at: string;
}

export interface DeckWithStats extends Deck {
  view_count: number;
  share_link_count: number;
}

export interface DeckShareLink {
  id: string;
  deck_id: string;
  user_id: string;
  share_token: string;
  url_slug: string | null;
  label: string | null;
  expires_at: string | null;
  password_hash: string | null;
  email_notifications: boolean;
  is_active: boolean;
  created_at: string;
}

export interface DeckView {
  id: string;
  deck_id: string;
  share_link_id: string | null;
  viewer_email: string | null;
  viewer_ip: string | null;
  user_agent: string | null;
  duration_seconds: number;
  viewed_at: string;
}

export interface DeckStats {
  total_decks: number;
  total_views: number;
  unique_viewers: number;
  active_links: number;
}

export interface DeckAnalytics {
  total_views: number;
  unique_viewers: number;
  average_duration: number;
  views_by_date: { date: string; count: number }[];
  recent_views: DeckView[];
}

// Input Types
export interface CreateShareLinkInput {
  label?: string;
  expires_at?: string;
  email_notifications?: boolean;
}

export interface UpdateShareLinkInput {
  label?: string;
  is_active?: boolean;
  email_notifications?: boolean;
}

export interface UpdateDeckInput {
  name?: string;
  description?: string;
}

// Supported file types for deck uploads
export const SUPPORTED_FILE_TYPES: Record<string, { ext: string; label: string; color: string }> = {
  pdf: { ext: 'pdf', label: 'PDF', color: 'rgb(239, 68, 68)' },
  pptx: { ext: 'pptx', label: 'PowerPoint', color: 'rgb(249, 115, 22)' },
  ppt: { ext: 'ppt', label: 'PowerPoint', color: 'rgb(249, 115, 22)' },
  doc: { ext: 'doc', label: 'Word', color: 'rgb(59, 130, 246)' },
  docx: { ext: 'docx', label: 'Word', color: 'rgb(59, 130, 246)' },
  png: { ext: 'png', label: 'PNG', color: 'rgb(34, 197, 94)' },
  jpg: { ext: 'jpg', label: 'JPEG', color: 'rgb(59, 130, 246)' },
};

export const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

// Helper to format file size
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Helper to get file type info
export const getFileTypeInfo = (fileType: string): { ext: string; label: string; color: string } => {
  return SUPPORTED_FILE_TYPES[fileType] || { ext: fileType, label: fileType.toUpperCase(), color: 'rgb(156, 163, 175)' };
};

// Slugify a name for use as the business segment in share URLs.
// Falls back to "untitled" when the result is empty (e.g. name is "!!!").
const slugifyName = (name: string): string => {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'untitled';
};

// Generate share URL from url_slug + deck name, or fall back to legacy token URL.
// urlSlugOrToken: pass link.url_slug for new links, link.share_token for legacy links.
// deckName: required for new slug-based format; omit only when using legacy token format.
export const getShareUrl = (urlSlugOrToken: string, deckName?: string): string => {
  const baseUrl = typeof window !== 'undefined'
    ? window.location.origin
    : 'https://sendupdates.ai';

  if (deckName && urlSlugOrToken.startsWith('pitch-deck-')) {
    return `${baseUrl}/decks/${slugifyName(deckName)}/${urlSlugOrToken}`;
  }
  // Legacy fallback for old 32-char hex tokens
  return `${baseUrl}/deck/${urlSlugOrToken}`;
};

// Format duration in seconds to human readable
export const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins < 60) return `${mins}m ${secs}s`;
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return `${hours}h ${remainingMins}m`;
};

// Format date for display
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Format date with time for display
export const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
